import { AJUDA_ARQUIVOS } from "@/lib/ajuda/dialogos/arquivos";
import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";

type TagRow = { id: string; name: string };

/** Gerencia as tags de um arquivo — busca o estado atual de forma independente (não recebe uma
 *  lista pronta), pra sempre refletir add/remove imediatamente sem depender de onde foi aberto. */
export function FileTagsDialog({ fileId, onClose }: { fileId: string | null; onClose: () => void }) {
  const utils = trpc.useUtils();
  const [newTagName, setNewTagName] = useState("");

  const fileQuery = trpc.file.getById.useQuery({ fileId: fileId ?? "" }, { enabled: !!fileId });
  const allTagsQuery = trpc.file.listTags.useQuery(undefined, { enabled: !!fileId });

  const invalidate = () => {
    utils.file.getById.invalidate({ fileId: fileId ?? "" });
    utils.file.invalidate();
  };

  const addTag = trpc.file.addTag.useMutation({
    onSuccess: invalidate,
    onError: (e) => toast.error(e.message || "Erro ao adicionar tag."),
  });
  const removeTag = trpc.file.removeTag.useMutation({
    onSuccess: invalidate,
    onError: (e) => toast.error(e.message || "Erro ao remover tag."),
  });
  const createTag = trpc.file.createTag.useMutation({
    onSuccess: (tag) => {
      if (fileId) addTag.mutate({ fileId, tagId: tag.id });
      setNewTagName("");
      utils.file.listTags.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao criar tag."),
  });

  const currentTags = fileQuery.data?.tags ?? [];
  const currentIds = new Set(currentTags.map((t) => t.tag.id));
  const availableTags = ((allTagsQuery.data ?? []) as TagRow[]).filter((t) => !currentIds.has(t.id));

  return (
    <Dialog open={!!fileId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm dark:bg-card">
        <DialogHeader>
          <DialogTitle className="truncate" ajuda={AJUDA_ARQUIVOS.tags}>Tags {fileQuery.data ? `— ${fileQuery.data.name}` : ""}</DialogTitle>
        </DialogHeader>

        {fileQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-1.5">
              {currentTags.length === 0 ? (
                <p className="text-xs text-zinc-400 dark:text-muted-foreground">Nenhuma tag ainda.</p>
              ) : (
                currentTags.map(({ tag }) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 dark:bg-primary/10 dark:text-primary"
                  >
                    {tag.name}
                    <button
                      type="button"
                      onClick={() => fileId && removeTag.mutate({ fileId, tagId: tag.id })}
                      className="hover:text-red-600"
                      aria-label={`Remover tag ${tag.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const trimmed = newTagName.trim();
                if (trimmed) createTag.mutate({ name: trimmed });
              }}
              className="flex gap-2"
            >
              <input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Criar nova tag..."
                className="h-9 flex-1 rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
              />
              <button
                type="submit"
                disabled={!newTagName.trim() || createTag.isPending}
                className="inline-flex h-9 items-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                Criar
              </button>
            </form>

            {availableTags.length > 0 && (
              <div>
                <p className="mb-1.5 text-[11px] uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
                  Tags existentes
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {availableTags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => fileId && addTag.mutate({ fileId, tagId: tag.id })}
                      className="rounded-full border border-zinc-200 px-2.5 py-1 text-xs text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted"
                    >
                      + {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
